/**
 * Custom Apps (My Apps) Query Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { customAppsService } from '../../services';
import { notify } from '../../shared/services';
import type {
  CreateUserCustomAppDto,
  UpdateUserCustomAppDto,
  UserCustomApp,
} from '../../types';
import { queryKeys } from './queryKeys';

export const useMyCustomApps = () => {
  return useQuery({
    queryKey: queryKeys.customApps.myCustomApps(),
    queryFn: () => customAppsService.getMyCustomApps(),
  });
};

export const useCreateCustomApp = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateUserCustomAppDto) => customAppsService.create(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.customApps.all });
      notify.success('Custom app added');
    },
  });
};

export const useUpdateCustomApp = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateUserCustomAppDto }) =>
      customAppsService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.customApps.all });
    },
  });
};

export const useDeleteCustomApp = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => customAppsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.customApps.all });
    },
  });
};

export const useToggleCustomAppFavorite = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => customAppsService.toggleFavorite(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.customApps.all });
      notify.success(data.isFavorite ? 'Added to favorites' : 'Removed from favorites');
    },
  });
};

/**
 * Persists a drag-and-drop reorder of Custom Apps.
 * Takes the full list already in its new order and PATCHes `displayOrder`
 * for every app whose position actually changed, with an optimistic
 * cache update so the grid reorders instantly.
 */
export const useReorderCustomApps = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (apps: UserCustomApp[]) => {
      await Promise.all(
        apps
          .map((app, index) => ({ app, index }))
          .filter(({ app, index }) => app.displayOrder !== index)
          .map(({ app, index }) =>
            customAppsService.update(app.id, { displayOrder: index }),
          ),
      );
    },
    onMutate: async (apps: UserCustomApp[]) => {
      const key = queryKeys.customApps.myCustomApps();
      const previous = queryClient.getQueryData<UserCustomApp[]>(key);
      queryClient.setQueryData(
        key,
        apps.map((app, index) => ({ ...app, displayOrder: index })),
      );
      return { previous };
    },
    onError: (_err, _apps, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKeys.customApps.myCustomApps(), context.previous);
      }
      notify.error('Failed to save app order');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.customApps.all });
    },
  });
};
